import React, { useEffect, useState } from 'react';
import { DatePicker } from 'antd';
import {
  getDashboardAdmin,
  getTotalActiveUsers,
} from '../../../../api/admin/post.api';
import { Line } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { CircleDollarSign, Star, User, UserPlus } from 'lucide-react';
import {
  AdminCard,
  AdminPageHeader,
  AdminSectionTitle,
  AdminStatCard,
  AdminStatGrid,
} from '../../ui';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

const { RangePicker } = DatePicker;

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Define data types for Dashboard
interface DashboardData {
  totalUserRequestCreator: number;
  totalUserRequestDonate: number;
  totalActiveUsersInDay: number;
  totalActiveUsersInWeek: number;
  totalActiveUsersInMonth: number;
  totalActiveUsersInYear: number;
  totalRegisteredUsersInDay: number;
  totalRegisteredUsersInMonth: number;
  totalRegisteredUsersInYear: number;
  totalUserCreator: number;
  totalUserCanReceiveDonation: number;
}

interface FilteredData {
  activeUsers: number[];
  newUsers: number[];
  dates: string[];
}

const DashboardAdmin: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'days'),
    dayjs(),
  ]);
  const [filteredData, setFilteredData] = useState<FilteredData>({
    activeUsers: [],
    newUsers: [],
    dates: [],
  });
  const [filteredChartData, setFilteredChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  const getDashboardData = async () => {
    try {
      const { data } = await getDashboardAdmin();
      setDashboardData(data);
    } catch (error) {
      console.error('Lỗi khi lấy dữ liệu từ API:', error);
    }
  };

  const getFilteredData = async (dates: [dayjs.Dayjs, dayjs.Dayjs]) => {
    try {
      const response = await getTotalActiveUsers({
        pageNumber: 1,
        pageSize: 100,
        fromDay: dates[0].toISOString(),
        toDay: dates[1].toISOString(),
      });
      setFilteredData({
        activeUsers: response.data.items.map(
          (item: any) => item.totalActiveUsersInDate
        ),
        newUsers: response.data.items.map((item: any) => item.newUsers), // Assuming newUsers is part of the response
        dates: response.data.items.map((item: any) =>
          dayjs(item.date).format('DD/MM/YYYY')
        ),
      });
    } catch (error) {
      console.error('Lỗi khi lấy dữ liệu theo ngày:', error);
    }
  };

  useEffect(() => {
    getDashboardData();
    getFilteredData(dateRange);
  }, []);

  useEffect(() => {
    setFilteredChartData({
      labels: filteredData.dates, // QA S-34: KHÔNG reverse (data không đảo → trục ngày lệch giá trị)
      datasets: [
        {
          label: 'Người dùng hoạt động',
          data: filteredData.activeUsers,
          borderColor: '#F6B92B',
          backgroundColor: '#F6B92B',
          fill: false,
          tension: 0.4,
        },
        {
          label: 'Người dùng đăng ký mới',
          data: filteredData.newUsers,
          borderColor: '#2271B1',
          backgroundColor: '#2271B1',
          fill: false,
          tension: 0.4,
        },
      ],
    });
  }, [filteredData]);
  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange(dates);
      getFilteredData(dates);
    }
  };

  if (!dashboardData) {
    return <p>Đang tải dữ liệu...</p>;
  }
  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Bảng điều khiển admin"
        description="Các yêu cầu đang chờ xử lý và lượng người dùng theo ngày."
      />

      <AdminStatGrid>
        <AdminStatCard
          icon={<UserPlus size={20} />}
          tone="danger"
          label="Yêu cầu Content Creator"
          value={dashboardData.totalUserRequestCreator}
        />
        <AdminStatCard
          icon={<CircleDollarSign size={20} />}
          tone="brand"
          label="Yêu cầu Donate"
          value={dashboardData.totalUserRequestDonate}
        />
        <AdminStatCard
          icon={<Star size={20} />}
          tone="success"
          label="Content Creators"
          value={dashboardData.totalUserCreator}
        />
        <AdminStatCard
          icon={<User size={20} />}
          tone="warning"
          label="User có thể nhận Donate"
          value={dashboardData.totalUserCanReceiveDonation}
        />
      </AdminStatGrid>

      <section className="adm-stack">
        <AdminSectionTitle
          title="Thống kê theo khoảng thời gian"
          extra={
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              format="DD/MM/YYYY"
            />
          }
        />
        <AdminCard title="Người dùng theo ngày">
          <ChartLine filteredChartData={filteredChartData} />
        </AdminCard>
      </section>
    </div>
  );
};

const ChartLine = ({ filteredChartData }: { filteredChartData: any }) => {
  return (
    <Line
      data={filteredChartData}
      options={{
        aspectRatio: 3,
        plugins: {
          legend: {
            position: 'top',
            align: 'end',
            labels: { usePointStyle: true, pointStyle: 'line' },
          },
        },
        elements: { point: { radius: 3 } },
      }}
    />
  );
};
export default DashboardAdmin;
